import { useLocation, useRoute } from 'wouter'
import { ChevronLeft, Calendar, User, Loader2, Share2, MapPin } from 'lucide-react'
import { ErrorBoundary } from '@/components/ErrorBoundary'
import { Button } from '@/components/ui/button'
import useSWR from 'swr'
import { apiFetch } from '@/utils'
import { useToast } from '@/hooks/use-toast'
import { getImageUrls } from '@/utils/imageUrls'
import GalleryGrid, { GalleryImage } from '@/components/GalleryGrid'
import { LazyLoadImage } from 'react-lazy-load-image-component'

interface NewsArticleDetail {
  id: number | string
  heading: string
  page_title?: string
  page_description?: string
  content?: string
  filename?: string
  filename_alt?: string
  page_url?: string
  published_date?: string
  category_id?: number | null
  author?: number
  gallery?: GalleryImage[]
}

const LoadingState = () => {
  return (
    <div className="flex-1 flex flex-col items-center justify-center">
      <Loader2 className="h-8 w-8 text-[#EF4444] animate-spin" />
      <p className="text-[#BABABB] text-sm mt-4">Loading article...</p>
    </div>
  )
}

const ErrorState = ({ message }: { message?: string }) => {
  const [, navigate] = useLocation()

  return (
    <div className="flex-1 flex flex-col items-center justify-center px-6">
      <p className="text-[#EF4444] text-center mb-4">
        {message || 'Failed to load article. Please try again later.'}
      </p>
      <Button
        onClick={() => navigate('/news')}
        className="bg-[#EF4444] hover:bg-[#DC2626] text-white"
      >
        Back to News
      </Button>
    </div>
  )
}

export default function ShowPage() {
  const [, navigate] = useLocation()
  const [match, params] = useRoute('/news/:id')
  const { toast } = useToast()

  const articleId = params?.id

  const { data, error, isLoading } = useSWR(
    articleId ? `/v1/secrets_hideaway/news/${articleId}` : null,
    (url: string) => apiFetch(url, { withCredentials: true }).then((res) => res.json())
  )

  const article: NewsArticleDetail | null = data?.success && data?.details?.items?.[0] ? data.details.items[0] : null
  const imageUrls = article?.filename ? getImageUrls('news', article.filename) : null

  const formattedDate = article?.published_date
    ? new Date(article.published_date).toLocaleDateString('en-US', {
      weekday: 'long',
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    })
    : null

  const handleShare = async () => {
    const url = window.location.href
    if (navigator.share) {
      try {
        await navigator.share({
          title: article?.heading || 'News Article',
          url
        })
      } catch (err) {
        // User cancelled or share failed
      }
    } else {
      await navigator.clipboard.writeText(url)
      toast({
        title: 'Link copied',
        description: 'Article link copied to clipboard'
      })
    }
  }

  return (
    <ErrorBoundary>
      <div className="min-h-auto bg-[#18181B] flex flex-col h-full">
        <div className="flex-1 max-w-5xl mx-auto w-full px-5 pb-8">
          {/* Header with back button */}
          <div
            role="banner"
            className="sticky top-0 z-50 bg-[#18181B] flex items-center justify-between text-white py-3 mb-[12px]"
          >
            <div className="flex items-center justify-start gap-2">
              <button
                type="button"
                aria-label="Go back"
                onClick={() => navigate('/news')}
                className="h-8 w-8 flex items-center justify-center rounded-full bg-white/10 hover:bg-white/20 focus:outline-none focus:ring-2 focus:ring-white/40"
              >
                <ChevronLeft className="h-[22px] w-[22px] text-[#ffffff]" />
              </button>

              <h1 className="text-[20px] leading-[18px] font-bold font-montserrat tracking-wide">
                News
              </h1>
            </div>

            {article && (
              <button
                type="button"
                aria-label="Share article"
                onClick={handleShare}
                className="h-8 w-8 flex items-center justify-center rounded-full bg-white/10 hover:bg-white/20 focus:outline-none focus:ring-2 focus:ring-white/40"
              >
                <Share2 className="h-[18px] w-[18px] text-[#ffffff]" />
              </button>
            )}
          </div>

          {/* Content */}
          {isLoading ? (
            <LoadingState />
          ) : error || !article ? (
            <ErrorState />
          ) : (
            <>
            {/* Title */}
            <h2 className="text-[#ffffff] font-semibold font-figtree text-[16px] leading-4 mb-4">
              {article.heading}
            </h2>

            {/* Meta Info */}


            {/* Featured Image */}
            {imageUrls && (
            <div className="relative w-full overflow-hidden rounded-xl mb-3 min-h-[234px]">
              <LazyLoadImage
                src={imageUrls.landscape}
                alt={article.filename_alt || article.heading}
                effect="opacity"
                wrapperClassName="lazy-image-wrapper"
                className="
                  w-full h-full object-cover
                  transition-transform duration-300
                  group-hover:scale-105
                "
              />

              <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/90 to-transparent px-2 pt-20 pb-[9px] text-white">
                <div className="h-px bg-gradient-to-r from-transparent via-gray-400 to-transparent my-2" />

                <div className="flex items-center justify-between text-[11px] font-medium">
                  {formattedDate && (
                    <div className="flex items-center gap-1">
                      <Calendar className="h-4 w-4" />
                      <span>{formattedDate}</span>
                    </div>
                  )}

                  <div className="flex items-center gap-1">
                    <MapPin className="h-4 w-4" />
                    <span>Secrets Hideaway</span>
                  </div>
                </div>
              </div>
            </div>
          )}

            {/* Summary */}
            {article.page_description && (
              <p className="text-[#ffffff] text-[16px] leading-[20px] mb-4 font-medium font-figtree">
                {article.page_description}
              </p>
            )}

            {/* Full Content */}
            {article.content && (
              <div className="prose prose-invert max-w-none">
                <div
                  className="text-[#BABABB] leading-relaxed font-figtree text-[13px] font-normal [&>p]:mb-3 [&>h2]:text-[#ffffff] [&>h2]:font-semibold [&>h2]:text-[16px] [&>h2]:mt-8 [&>h2]:mb-4 [&>h3]:text-[#ffffff] [&>h3]:font-semibold [&>h3]:text-[16px] [&>h3]:mt-6 [&>h3]:mb-3 [&>ul]:list-disc [&>ul]:pl-6 [&>ul]:mb-4 [&>ol]:list-decimal [&>ol]:pl-6 [&>ol]:mb-4 [&>a]:text-[#EF4444] [&>a]:hover:text-[#DC2626]"
                  dangerouslySetInnerHTML={{ __html: article.content }}
                />
              </div>
            )}

            {/* Gallery Grid */}
            {article.gallery && article.gallery.length > 0 && (
              <GalleryGrid images={article.gallery} title="Gallery" />
            )}
            </>
          )}
        </div>
      </div>
    </ErrorBoundary>
  )
}
